// src/components/WorkoutTimer.js
import React, { useEffect, useRef, useState } from 'react';

function WorkoutTimer({ timer, setTimer, workout }) {
  // Use a ref to store the interval ID for proper cleanup
  const intervalRef = useRef(null);
  // Track if we're in rest period
  const [isResting, setIsResting] = useState(false);
  // Store notification sound
  const soundRef = useRef(null);
  
  // Initialize the sound
  useEffect(() => {
    // Simple beep sound
    soundRef.current = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLHPM+N2TQwUHD0rM/fXLc0ASAA8YV7TgbL/z88J8WwkAF1Kq0E/Hsf/rwptqGwMie8i6Ol/a/9eHdVsVADup472ESfn/olp3xIMkAAuFxu9s+uhFwNtdBQAvo+1v//ztbLDBDUMAAWXz3pQQCSYvvuBoGgIDAIT03EYsJB8rLqTqbgkAAABu//78SlYNAQAYSKQBAAAAAAD//7bac0BLBwAAAAAAABwI4jCbabqWz5LJyrRoUQlZRq/u/9JlF/+9BWJRChUt58LO/f+9FjJ7oj8AAGs10f/gQBv68OF+EtJWL2O850UYGYHY5jI3XmdJj+qxIPdS3O6ZLDDii8X1//nC5f8LpcbOzKvM+a6If/19CyxPteWHouNAT0RHa12wpK');
    
    return () => {
      if (soundRef.current) {
        soundRef.current.pause();
        soundRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Clear any existing interval when effect runs
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (timer.isActive && !timer.isPaused) {
      intervalRef.current = setInterval(() => {
        setTimer(prev => ({
          ...prev,
          time: prev.time > 0 ? prev.time - 1 : 0
        }));
      }, 1000);
    }
    
    // Check if timer reached zero
    if (timer.time === 0 && timer.isActive) {
      // Play sound
      try {
        if (soundRef.current) {
          soundRef.current.currentTime = 0;
          soundRef.current.play().catch(error => console.log('Audio play failed', error));
        }
      } catch (error) {
        console.error('Error playing audio:', error);
      }
      
      // If we're resting, move to the next exercise
      if (isResting) {
        setIsResting(false);
        
        if (workout && timer.currentExerciseIndex < workout.exercises.length) {
          setTimer(prev => ({
            ...prev,
            currentExerciseIndex: prev.currentExerciseIndex + 1,
            time: prev.currentExerciseIndex + 1 < workout.exercises.length 
              ? workout.exercises[prev.currentExerciseIndex + 1].duration * 60 
              : 0
          }));
        } else {
          // End of workout
          setTimer(prev => ({
            ...prev,
            isActive: false,
            isPaused: true,
          }));
        }
      } else {
        // If we're not resting, start rest period
        setIsResting(true);
        setTimer(prev => ({
          ...prev,
          time: workout.restTime
        }));
      }
    }
    
    // Return cleanup function
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timer, workout, setTimer, isResting]);
  
  const startTimer = () => {
    setTimer(prev => ({
      ...prev,
      isActive: true,
      isPaused: false
    }));
  };
  
  const pauseTimer = () => {
    setTimer(prev => ({
      ...prev,
      isPaused: true
    }));
  };
  
  const resetTimer = () => {
    setIsResting(false);
    if (workout && timer.currentExerciseIndex < workout.exercises.length) {
      setTimer(prev => ({
        ...prev,
        isActive: false,
        isPaused: true,
        time: workout.exercises[timer.currentExerciseIndex].duration * 60
      }));
    }
  };
  
  const skipToNext = () => {
    setIsResting(false);
    if (workout && timer.currentExerciseIndex < workout.exercises.length - 1) {
      setTimer(prev => ({
        ...prev,
        isActive: false,
        isPaused: true,
        time: workout.exercises[prev.currentExerciseIndex + 1].duration * 60,
        currentExerciseIndex: prev.currentExerciseIndex + 1
      }));
    }
  };
  
  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };
  
  // Calculate progress percentage
  const calculateProgress = () => {
    if (isResting) {
      return ((workout.restTime - timer.time) / workout.restTime) * 100;
    }
    
    if (timer.currentExerciseIndex < workout.exercises.length) {
      const totalTime = workout.exercises[timer.currentExerciseIndex].duration * 60;
      return ((totalTime - timer.time) / totalTime) * 100;
    }
    
    return 0;
  };
  
  const progress = calculateProgress();
  const currentExercise = timer.currentExerciseIndex < workout.exercises.length 
    ? workout.exercises[timer.currentExerciseIndex] 
    : null;
  
  const isWorkoutComplete = timer.currentExerciseIndex >= workout.exercises.length;
  
  return (
    <div className={`p-5 rounded-lg shadow-sm border ${
      isResting ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'
    }`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3">
        <div className="mb-2 md:mb-0">
          <h3 className="font-semibold text-lg flex items-center">
            {isResting ? (
              <>
                <span className="mr-2">⏱️</span>
                <span className={isResting ? 'text-green-800' : 'text-blue-800'}>
                  Rest Time
                </span>
              </>
            ) : isWorkoutComplete ? (
              <>
                <span className="mr-2">🎉</span>
                <span className="text-blue-800">Workout Complete!</span>
              </>
            ) : (
              <>
                <span className="mr-2">{currentExercise?.image || '🏋️‍♀️'}</span>
                <span className="text-blue-800">
                  {currentExercise?.name || 'Current Exercise'}
                </span>
              </>
            )}
          </h3>
          {!isWorkoutComplete && (
            <p className={`text-sm ${isResting ? 'text-green-700' : 'text-blue-700'}`}>
              {isResting ? 'Rest before next exercise' : currentExercise?.description?.slice(0, 80) + '...'}
            </p>
          )}
        </div>
        <div className="flex items-center bg-white rounded-full border border-gray-200 px-4 py-2 shadow-sm">
          <div className={`text-2xl font-mono font-bold ${
            isResting ? 'text-green-600' : 'text-blue-600'
          }`}>
            {formatTime(timer.time)}
          </div>
          {!isResting && currentExercise && (
            <div className="ml-2 text-xs text-gray-500">
              of {currentExercise.duration}:00
            </div>
          )}
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
        <div 
          className={`h-full ${isResting ? 'bg-green-500' : 'bg-blue-600'} transition-all duration-1000 ease-linear`}
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      
      <div className="flex justify-center space-x-3">
        {!timer.isActive || timer.isPaused ? (
          <button 
            onClick={startTimer}
            disabled={isWorkoutComplete}
            className={`px-4 py-2 rounded-md flex items-center ${
              isWorkoutComplete 
                ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                : 'bg-green-600 hover:bg-green-700 text-white shadow-sm'
            }`}
            aria-label={timer.isPaused ? 'Resume timer' : 'Start timer'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {timer.isPaused ? 'Resume' : 'Start'}
          </button>
        ) : (
          <button 
            onClick={pauseTimer}
            className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-md shadow-sm flex items-center"
            aria-label="Pause timer"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Pause
          </button>
        )}
        
        <button 
          onClick={resetTimer}
          disabled={isWorkoutComplete}
          className={`px-4 py-2 rounded-md flex items-center ${
            isWorkoutComplete 
              ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
              : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
          }`}
          aria-label="Reset timer"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Reset
        </button>
        
        <button 
          onClick={skipToNext}
          disabled={isWorkoutComplete || timer.currentExerciseIndex >= workout.exercises.length - 1}
          className={`px-4 py-2 rounded-md flex items-center ${
            isWorkoutComplete || timer.currentExerciseIndex >= workout.exercises.length - 1 
              ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
              : 'bg-purple-600 hover:bg-purple-700 text-white shadow-sm'
          }`}
          aria-label="Skip to next exercise"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
          Skip
        </button>
      </div>
      
      {!isWorkoutComplete && (
        <div className="mt-4 text-center text-sm text-gray-500">
          {timer.currentExerciseIndex + 1} of {workout.exercises.length} exercises
        </div>
      )}
    </div>
  );
}

export default WorkoutTimer;